import React, { useState, useEffect } from "react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import { useLoading } from "../../contexts/LoadingContext";
import "../../styles/scss/openNotice.scss";

function OpenNoticePage() {
  useLoginCheck();
  const { setIsLoading } = useLoading();
  const [studios, setStudios] = useState([]);
  // 送信前プレビュー: { studio, subject, html, recipient_count, request_count }
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  // 店舗カードの開閉状態（studio_id → true なら閉じている）
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = (studioId) =>
    setCollapsed((prev) => ({ ...prev, [studioId]: !prev[studioId] }));

  // 予約開始リクエスト一覧取得（店舗単位に集約済み）
  const fetchOpenNoticeData = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_open_notices/`);
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setStudios(data.studios || []);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOpenNoticeData();
  }, []);

  // 送信内容プレビューを開く（実際のメール本文をサーバーで生成して取得）
  const handleOpenPreview = async (studio) => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/preview_reengagement/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studio_id: studio.studio_id }),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "プレビューの取得に失敗しました");
      }
      setPreview({ type: "reengage", studio, ...data });
    } catch (error) {
      console.error("プレビューの取得に失敗しました:", error);
      alert(`プレビューの取得に失敗しました: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleClosePreview = () => {
    if (!sending) setPreview(null);
  };

  // プレビューの内容で実送信
  const handleConfirmSend = async () => {
    if (!preview) return;
    const studio = preview.studio;
    setSending(true);
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/send_reengagement/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ studio_id: studio.studio_id }),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "ネットワークレスポンスが異常です。");
      }
      if (data.status === "no_target") {
        alert("送信対象がありませんでした。");
      } else {
        alert(`${data.sent_count} 件に送信しました`);
      }
      setPreview(null);
      fetchOpenNoticeData();
    } catch (error) {
      console.error("送信中にエラーが発生しました:", error);
      alert(`送信に失敗しました: ${error.message}`);
    }
    setSending(false);
    setIsLoading(false);
  };

  // 従来の再開通知（表示中かつ受付中になったレッスン）: プレビューを開く
  const handleOpenReopenPreview = async (studio, notice) => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/preview_open_notice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: notice.id,
          mail: notice.mail,
          studio_id: studio.studio_id,
          lesson_id: notice.lesson_id,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "プレビューの取得に失敗しました");
      }
      setPreview({
        type: "reopen",
        studio_name: studio.studio_name || `店舗ID:${studio.studio_id}`,
        studioId: studio.studio_id,
        notice,
        mail: notice.mail,
        subject: data.subject,
        html: data.html,
      });
    } catch (error) {
      console.error("プレビューの取得に失敗しました:", error);
      alert(`プレビューの取得に失敗しました: ${error.message}`);
    }
    setIsLoading(false);
  };

  // 再開通知: プレビューの内容で実送信
  const handleConfirmReopen = async () => {
    if (!preview) return;
    setSending(true);
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/send_open_notice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: preview.notice.id,
          mail: preview.notice.mail,
          studio_id: preview.studioId,
          lesson_id: preview.notice.lesson_id,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "ネットワークレスポンスが異常です。");
      }
      alert("メールを送信しました");
      setPreview(null);
      fetchOpenNoticeData();
    } catch (error) {
      console.error("送信中にエラーが発生しました:", error);
      alert(`送信に失敗しました: ${error.message}`);
    }
    setSending(false);
    setIsLoading(false);
  };

  // 予約開始リクエストを削除（論理削除）
  const handleDelete = async (row) => {
    if (!window.confirm(`このリクエストを削除しますか？\n${row.mail}`)) return;
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/delete_open_notice/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: row.id }),
      });
      const data = await response.json();
      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "削除に失敗しました");
      }
      fetchOpenNoticeData();
    } catch (error) {
      console.error("削除中にエラーが発生しました:", error);
      alert(`削除に失敗しました: ${error.message}`);
    }
    setIsLoading(false);
  };

  // 会員バッジ（在籍中=入会済 を優先、退会者=退会済）
  const memberBadge = (row) => {
    if (row.enrolled) return <span className="pill-enrolled">入会済</span>;
    if (row.withdrawn) return <span className="pill-withdrawn">退会済</span>;
    return null;
  };

  const totalStranded = studios.reduce((sum, s) => sum + s.counts.stranded, 0);

  return (
    <div className="management-open-notice">
      <div className="page-header">
        <h1>予約開始リクエスト</h1>
      </div>

      <p className="lead t-body">
        レッスンの作り直しや担当変更で
        <b>宙に浮いたリクエスト（website に表示されなくなったレッスン）</b>
        を店舗単位でまとめ、体験レッスンのご案内を一斉送信できます。 現在の対象は合計{" "}
        <b className="t-accent">{totalStranded}</b> 件です。
      </p>

      {studios.length === 0 && (
        <p className="empty-note">未送信のリクエストはありません。</p>
      )}

      <div className="studio-list">
        {studios.map((studio) => {
          const canSend =
            studio.counts.stranded > 0 &&
            studio.studio_active &&
            studio.bookable_lessons.length > 0;
          const isCollapsed = !!collapsed[studio.studio_id];

          return (
            <div className="panel" key={studio.studio_id}>
              <div
                className="panel-toolbar"
                role="button"
                tabIndex={0}
                onClick={() => toggleCollapse(studio.studio_id)}
              >
                <div className="toolbar-left">
                  <span
                    className={`caret${isCollapsed ? "" : " open"}`}
                    aria-hidden="true"
                  />
                  <h2 className="studio-name">
                    {studio.studio_name || `店舗ID:${studio.studio_id}`}
                  </h2>
                  {!studio.studio_active && (
                    <span className="tag tag-muted">非公開店舗</span>
                  )}
                  <span className="tag tag-stranded">
                    宙に浮いた {studio.counts.stranded}
                  </span>
                  <span className="tag tag-reopened">
                    再開 {studio.counts.reopened}
                  </span>
                  <span className="tag tag-waiting">
                    受付終了中 {studio.counts.waiting}
                  </span>
                </div>
                {studio.counts.stranded > 0 && (
                  <div className="toolbar-right">
                    <button
                      className="send-btn"
                      disabled={!canSend}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPreview(studio);
                      }}
                    >
                      送信内容をプレビュー（{studio.stranded_recipient_count} 宛先）
                    </button>
                  </div>
                )}
              </div>

              <div
                className={`panel-body${isCollapsed ? " collapsed" : ""}`}
              >
                {/* 宙に浮いた層（再エンゲージ対象） */}
                {studio.counts.stranded > 0 && (
                  <div className="block">
                    <div className="block-title accent">
                      宙に浮いたリクエスト（再エンゲージ対象）
                    </div>

                    {!studio.studio_active && (
                      <p className="note-warn">
                        ※ 非公開の店舗のため送信できません。
                      </p>
                    )}
                    {studio.studio_active &&
                      studio.bookable_lessons.length === 0 && (
                        <p className="note-warn">
                          ※ 現在この店舗に受付中のレッスンがないため送信できません。
                        </p>
                      )}

                    <p className="summary t-muted">
                      リクエスト {studio.counts.stranded} 件 ／ 送信先{" "}
                      {studio.stranded_recipient_count} 件（重複除去）
                    </p>

                    <div className="req-table-wrap">
                      <table className="req-table">
                        <thead>
                          <tr>
                            <th>メールアドレス</th>
                            <th>リクエストしたレッスン</th>
                            <th>リクエスト日時</th>
                            <th className="th-action">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {studio.stranded.map((r) => (
                            <tr key={r.id}>
                              <td className="c-mail">
                                {r.mail}
                                {memberBadge(r)}
                              </td>
                              <td className="c-lesson">{r.lesson_text || "—"}</td>
                              <td className="c-date">
                                {r.created_at_display || "—"}
                              </td>
                              <td className="c-action">
                                <button
                                  className="del-btn"
                                  onClick={() => handleDelete(r)}
                                >
                                  削除
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 従来の再開（表示中かつ受付中: 個別送信） */}
                {studio.reopened.length > 0 && (
                  <div className="block">
                    <div className="block-title">
                      再開（元のレッスンが受付中に戻りました）
                    </div>
                    <ul className="rows">
                      {studio.reopened.map((n) => (
                        <li key={n.id}>
                          <span className="mail">{n.mail}</span>
                          {memberBadge(n)}
                          <span className="lesson t-muted">{n.lesson_text}</span>
                          <span className="date t-faint">
                            {n.created_at_display}
                          </span>
                          <button
                            className="row-btn"
                            onClick={() => handleOpenReopenPreview(studio, n)}
                          >
                            再開通知をプレビュー
                          </button>
                          <button
                            className="del-btn"
                            onClick={() => handleDelete(n)}
                          >
                            削除
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 受付終了中（満席・待機） */}
                {studio.waiting.length > 0 && (
                  <div className="block">
                    <div className="block-title">受付終了中（満席・待機）</div>
                    <ul className="rows">
                      {studio.waiting.map((n) => (
                        <li key={n.id}>
                          <span className="mail t-muted">{n.mail}</span>
                          {memberBadge(n)}
                          <span className="lesson t-faint">{n.lesson_text}</span>
                          <span className="date t-faint">
                            {n.created_at_display}
                          </span>
                          <span className="status t-faint">受付終了中</span>
                          <button
                            className="del-btn"
                            onClick={() => handleDelete(n)}
                          >
                            削除
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 送信前プレビュー モーダル */}
      {preview && (
        <div className="preview-overlay" onClick={handleClosePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-head">
              <h3>
                {preview.type === "reopen"
                  ? "再開通知の確認"
                  : "送信内容の確認"}{" "}
                — {preview.studio_name}
              </h3>
              <button
                className="preview-close"
                onClick={handleClosePreview}
                disabled={sending}
              >
                ×
              </button>
            </div>

            <div className="preview-meta">
              <div>
                <span className="k">送信先</span>
                <span className="v">
                  {preview.type === "reopen"
                    ? preview.mail
                    : `${preview.recipient_count} 件（重複除去）／ 対象リクエスト ${preview.request_count} 件`}
                </span>
              </div>
              <div>
                <span className="k">件名</span>
                <span className="v">{preview.subject}</span>
              </div>
            </div>

            <div className="preview-body">
              <iframe
                title="email-preview"
                className="preview-frame"
                srcDoc={preview.html}
              />
            </div>

            <div className="preview-actions">
              <button
                className="btn-cancel"
                onClick={handleClosePreview}
                disabled={sending}
              >
                キャンセル
              </button>
              {preview.type === "reopen" ? (
                <button
                  className="btn-send"
                  onClick={handleConfirmReopen}
                  disabled={sending}
                >
                  {sending ? "送信中..." : "この内容で送信"}
                </button>
              ) : (
                <button
                  className="btn-send"
                  onClick={handleConfirmSend}
                  disabled={sending || preview.recipient_count === 0}
                >
                  {sending
                    ? "送信中..."
                    : `この内容で ${preview.recipient_count} 件に送信`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenNoticePage;
